import React from "react";
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridEventListener,
    GridRowEditStopReasons, GridRowId, GridRowModel, GridRowModes,
    GridRowModesModel
} from "@mui/x-data-grid";
import {
    Cancel,
    Edit,
    Save
} from "@mui/icons-material";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PostAddIcon from "@mui/icons-material/PostAdd";
import {QuickFilterToolBar} from "../CheckIn";



function formatPlayerName(fullName: string): string {
    const parts = fullName.trim().split(/\s+/)
    if (parts.length < 2) return parts[0] ?? fullName
    return `${parts[0]} ${parts[parts.length - 1][0]}.`
}

function generatePostText(rows: any[]): string {
    const groups = new Map<string, string[]>()
    for (const row of rows) {
        if (!row.assigned) continue
        if (!groups.has(row.assigned)) groups.set(row.assigned, [])
        groups.get(row.assigned)!.push(formatPlayerName(row.playerName))
    }
    const TAIL = ['Other On-Site', 'Off-Site']
    return [...groups.keys()]
        .sort((a, b) => {
            const ai = TAIL.indexOf(a), bi = TAIL.indexOf(b)
            if (ai !== -1 && bi !== -1) return ai - bi
            if (ai !== -1) return 1
            if (bi !== -1) return -1
            return a.localeCompare(b)
        })
        .map(loc => {
            const names = [...groups.get(loc)!].sort((a, b) => a.localeCompare(b))
            return `# ${loc}\n${names.join('\n')}`
        })
        .join('\n\n')
}

export default function CabinDataGrid(params: any) {
    const locations: string[] = [
        ...(params.locations ?? []).map((l: any) => l.name),
        'Other On-Site',
        'Off-Site',
    ];
    const [rows, setRows] = React.useState(params.assignments);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [postOpen, setPostOpen] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const handleRowEditStop: GridEventListener<'rowEditStop'> = (eParams, event) => {
        if (eParams.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }

    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});

    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow!.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const updatedRow = {...newRow};
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

        params.onEdit(updatedRow)
        return updatedRow;
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };


    const columns: GridColDef[] = [
        {
            field: 'characterName', headerName: 'Character'
        },
        {
            field: 'playerName', headerName: 'Player',
        },
        {
            field: 'requested', headerName: 'Requested', align: "center", headerAlign: "center",width:300
        },
        {
            field: 'assigned', headerName: 'Assignment', align: "center", headerAlign: "center",
            type: 'singleSelect',
            valueOptions: locations,
            editable: true,
            width: 250
        }, {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<Save/>}
                            label="Save"
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<Cancel/>}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<Edit/>}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />
                ];
            }
        }
    ]

    const postText = generatePostText(rows)

    function handleCopy() {
        navigator.clipboard.writeText(postText).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    return (
        <React.Fragment>
            <h2>Cabin Assignments</h2>
            <Button
                variant="outlined"
                size="small"
                startIcon={<PostAddIcon/>}
                onClick={() => setPostOpen(true)}
                sx={{mb: 1}}
            >
                Create Post
            </Button>
            <DataGrid slots={{toolbar: QuickFilterToolBar}}
                      editMode="row"
                      rowModesModel={rowModesModel}
                      onRowModesModelChange={handleRowModesModelChange}
                      onRowEditStop={handleRowEditStop}
                      processRowUpdate={processRowUpdate}
                      autoHeight={true}
                      disableColumnMenu={true}
                      rows={rows}
                      columns={columns}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      density={"compact"}
                      getRowHeight={() => 'auto'}
                      autosizeOptions={{includeOutliers: true}}
            />
            <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cabin Assignment Post</DialogTitle>
                <DialogContent>
                    <pre style={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                        whiteSpace: 'pre-wrap',
                        margin: 0,
                        lineHeight: 1.6,
                    }}>
                        {postText || '(No assignments yet)'}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button
                        startIcon={<ContentCopyIcon/>}
                        onClick={handleCopy}
                        variant="contained"
                        disabled={!postText}
                    >
                        {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Button onClick={() => setPostOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}